<template>
  <journey-builder-workflows-form
    v-if="shouldRender"
    ref="workflowsFormComponent"
    title="Upload files (optional)"
    subtitle="Upload any onboarding documents, information on company culture, and more to tailor your Journey"
    :is-save-disabled="isSaveDisabled"
    back-url="/prebuilt_journeys"
    back-text="Cancel"
    @save="submitForm(false)"
    @moveBottomSection="onMoveBottomSection"
  >
    <template #topSection>
      <donut-upload
        class="w-100p"
        :records="workflow.assets"
        :allow-multiple="true"
        :use-upload-box-style="true"
        files-list-position="right"
        :permitted-file-types="['.pdf']"
        :size-limit="totalFileSizeLimit"
        :individual-file-size-limit="individualFileSizeLimit"
      />
    </template>
    <template #bottomSection>
      <div
        ref="textAreaWrapper"
        class="w-80p fc-ac"
      >
        <donut-input-text
          v-model="workflow.user_context"
          placeholder="Write or paste in additional information"
          :textarea="true"
          :autogrow="true"
          :max-length="10000"
          :text-area-max-height="textAreaMaxHeight"
          :wysiwyg="false"
        />
      </div>
    </template>
    <template #secondaryAction>
      <a
        v-if="!workflow.id"
        :class="{'link': true, 'disabled': state.formSubmitted}"
        @click="submitForm(true)"
      >
        Skip for now
      </a>
    </template>
  </journey-builder-workflows-form>
</template>

<script setup>
  import { computed, onBeforeMount, onMounted, ref } from 'vue';
  import { useStore } from 'vuex';
  import useClientState from '../../../composables/client-state';
  import useJourneyBuilderFormSubmission from '../../../composables/journey-builder/form-submission';

  const { commit, dispatch, getters, state } = useStore();
  const { getSessionStorage, removeSessionStorage } = useClientState();
  const { submitJourneyBuilderForm } = useJourneyBuilderFormSubmission('parsed_user_inputs', (workflow) => `/journey_builder/${workflow.id}/goals`);

  const props = defineProps({
    workflow: { type: Object, required: true },
    reauthData: { type: Object, default: null },
  });
  const workflow = ref(props.workflow);
  const shouldRender = ref(true);
  const textAreaMaxHeight = ref(null);
  const textAreaWrapper = ref(null);
  const workflowsFormComponent = ref(null);

  // File size limits in bytes (using MB = 1000*1000)
  // OpenAI API has a limit of 10MB per file and 32MB total
  const individualFileSizeLimit = 10 * 1000 * 1000; // 10MB
  const totalFileSizeLimit = 32 * 1000 * 1000; // 32MB

  const uploadedFileIds = computed(() => getters['uploads/uploadedFiles'].map(f => f.id));

  const isSaveDisabled = computed(() => !uploadedFileIds.value.length && !workflow.value.user_context);

  const submitForm = (skipForNow) => {
    const formData = {
      workflow_id: workflow.value.id,
      workflow: {
        user_context: (skipForNow ? null : workflow.value.user_context),
        asset_ids: (skipForNow ? [] : uploadedFileIds.value),
      },
    };
    dispatch('setToast', { text: 'Processing...', duration: 30000, isLoading: true });
    submitJourneyBuilderForm('/journey_builder/submit_user_inputs', formData);
  };

  onBeforeMount(() => {
    // If the id is in sessionStorage, the user probably submitted this page then went back
    // in their browser. Take them to this page for that workflow to restore their data.
    const workflowData = JSON.parse(getSessionStorage('journeyBuilderWorkflow'));
    if (workflowData) {
      shouldRender.value = false; // Do not render while reloading.
      removeSessionStorage('journeyBuilderWorkflow');
      // We *could* just render the page using the workflowData, but it's nice to send them to the url with the id.
      window.location.replace(`${window.location.pathname}?workflow_id=${workflowData.id}`);
    }
  });

  onMounted(() => {
    if (props.reauthData) {
      commit('update', { module: 'reauthing', key: 'slackAuth', value: props.reauthData });
    }
    if (getters['reauthing/slackScopeMissing']('channels_manage')) {
      dispatch('reauthing/openPermissionsModal', {
        source: 'Journey Builder (user inputs)',
        missingScope: 'default', // A fake truthy value to use the generic fallback message.
        dismissable: false,
      });
    }
  });

  const onMoveBottomSection = () => {
    const { topSection } = workflowsFormComponent.value.$refs;
    const newMaxHeight = textAreaWrapper.value.getBoundingClientRect().bottom - topSection.getBoundingClientRect().bottom - 16;
    textAreaMaxHeight.value = `${newMaxHeight}px`;
  };
</script>
